import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import TotpCodeField, { isCompleteCode } from '../TotpCodeField';
import {
  startEnrollment,
  completeEnrollment,
  getMfaStatus,
  InvalidCredentialsError,
  InvalidCodeError,
  AlreadyEnrolledError,
  SessionExpiredError,
} from '../../services/mfaService';

const PASSWORD_STEP = 'password';
const VERIFY_STEP = 'verify';

// The client owns this sentence rather than the server. See handleComplete.
const WRONG_CODE_MESSAGE = "That code isn't right — check your authenticator app and try again.";

const formatRemaining = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * A read-only value with a copy button — the manual-entry fallback for anyone
 * who cannot scan the QR code.
 */
const CopyableField = ({ label, value, id }) => {
  const [copied, setCopied] = useState(false);

  // The "Copied" tooltip lapses on a timer rather than in the click handler, so
  // the timer belongs to an effect that can clear it: copying the setup key and
  // cancelling the dialog inside the two seconds would otherwise leave a
  // setState pointed at a component that is already gone.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // A denied clipboard permission is not worth an error state: the value
      // is on screen and selectable, which is the fallback's fallback.
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
      <TextField
        id={id}
        label={label}
        value={value}
        fullWidth
        size="small"
        slotProps={{ htmlInput: { readOnly: true, style: { fontFamily: 'monospace' } } }}
      />
      <Tooltip title={copied ? 'Copied' : `Copy ${label.toLowerCase()}`}>
        <IconButton onClick={copy} aria-label={`Copy ${label.toLowerCase()}`} sx={{ mt: 0.5 }}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/**
 * Turning MFA on: password re-entry, then a QR code and a verification code.
 *
 * ## The ambiguity this dialog exists to work around
 *
 * `403 INVALID_CODE` has **three** causes here, all with the same message,
 * because translateIdentityError collapses the provider's errors onto one
 * sentinel:
 *
 *   a. the user mistyped the code;
 *   b. the enrollment `session_info` expired;
 *   c. the code was right when read but went stale inside its own 30-second
 *      TOTP step before it arrived.
 *
 * The countdown rules out **(b) only** — once the clock runs out, submit is
 * disabled and the panel switches to "start over" before a request can be sent,
 * so a rejection that does arrive cannot be a dead session. It says nothing
 * about (a) versus (c), and no client-side signal can separate those two.
 *
 * The deadline it counts down to arrives from mfaService already finished:
 * `expiresAt` is epoch milliseconds measured against *this browser's* clock,
 * with the client/server skew cancelled at the boundary using the `Date` header
 * of the response that carried it. So there is no resolving to do here, and
 * deliberately nothing to resolve — the server's own reading never reaches this
 * file, because comparing one directly against `Date.now()` is how a browser
 * running fast sees a freshly issued window as already dead and can never enrol
 * at all. Identical to how MfaCodeStep is handed the login challenge's window;
 * see expiryWindow for why either is needed.
 */
function EnrollmentDialog({ open, onClose, onEnrolled }) {
  const [step, setStep] = useState(PASSWORD_STEP);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [codeFailures, setCodeFailures] = useState(0);

  // Kept in a ref as well so the reset on close cannot be defeated by a stale
  // closure: the password is held in memory only for as long as the dialog is
  // open, because completing enrollment needs it a second time.
  const resetRef = useRef();

  // Which attempt a reply belongs to. Every request reads this before it starts
  // and checks it again before each setState, because reset() bumps it — so
  // "Start over" and closing the dialog both orphan whatever is still in
  // flight. Without it, a slow completion landing after the user walked away
  // would call onEnrolled and pop the green notice for an enrollment they
  // abandoned, or paint "Your password was changed" over the password step they
  // are already back on; and reset() setting busy to false would have re-opened
  // Cancel and the backdrop while the request was still running.
  const generation = useRef(0);

  const reset = useCallback(() => {
    generation.current += 1;
    setStep(PASSWORD_STEP);
    setPassword('');
    setCode('');
    setSession(null);
    setDeadline(null);
    setRemainingMs(0);
    setBusy(false);
    setPasswordError(null);
    setCodeError(null);
    setFormError(null);
    setCodeFailures(0);
  }, []);
  resetRef.current = reset;

  // Clear every secret the moment the dialog goes away, however it went away.
  useEffect(() => {
    if (!open) resetRef.current();
  }, [open]);

  const expired = deadline !== null && remainingMs <= 0;

  useEffect(() => {
    if (deadline === null) return undefined;

    const tick = () => setRemainingMs(deadline - Date.now());
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  const handleStart = async (event) => {
    event.preventDefault();
    const issued = generation.current;
    setBusy(true);
    setPasswordError(null);
    setFormError(null);

    try {
      const started = await startEnrollment(password);
      if (issued !== generation.current) return;
      const until = started.expiresAt;
      setSession(started);
      setDeadline(until);
      // Seed the remaining time in the same batch as the deadline. React 18
      // batches these updates into one render, and the effect that starts the
      // countdown runs only after that render has painted — so leaving
      // remainingMs at 0 here paints one frame of "this session expired" over
      // the QR code, to someone who just typed their password correctly.
      setRemainingMs(until - Date.now());
      setStep(VERIFY_STEP);
    } catch (err) {
      if (issued !== generation.current) return;

      if (err instanceof InvalidCredentialsError) {
        setPasswordError(err.message);
      } else if (err instanceof AlreadyEnrolledError) {
        // Defensive: the tab checked GET /mfa before offering Enable, but
        // another tab or device may have enrolled since. Nothing is wrong — the
        // user has what they wanted, so refresh and close rather than error.
        onEnrolled(err.message);
      } else if (err instanceof SessionExpiredError) {
        // The redirect is already underway; this dialog is on its way out.
      } else {
        // A 429 lands here too, deliberately. TooManyAttemptsError carries a
        // sentence that reads perfectly well in this banner, and there is
        // nothing the dialog can do differently about it — no retry timer to
        // start, since the response carries no Retry-After. Its own arm would
        // have been the generic one written twice.
        setFormError(err.message);
      }
    } finally {
      if (issued === generation.current) setBusy(false);
    }
  };

  const handleComplete = async (event) => {
    event.preventDefault();
    const issued = generation.current;
    setBusy(true);
    setCodeError(null);
    setFormError(null);

    try {
      await completeEnrollment({
        password,
        sessionInfo: session.session_info,
        code,
      });
      if (issued !== generation.current) return;
      onEnrolled('Multi-factor authentication is on.');
    } catch (err) {
      if (issued !== generation.current) return;

      if (err instanceof InvalidCodeError) {
        // Two tabs can both pass the already-enrolled guard and both receive a
        // QR code (mfa_service.go:67-91 takes no lock between the cache check
        // and the provider check). The loser's completion fails as
        // INVALID_CODE even though the code was right. Rather than word an
        // error that is honest but baffling, ask the server what is actually
        // true: if MFA is now on, the user got what they came for.
        //
        // The same re-check absorbs a double submit and a 204 whose response
        // was lost in flight. It costs one GET on a path that already failed.
        try {
          const status = await getMfaStatus();
          if (issued !== generation.current) return;
          if (status.enrolled) {
            onEnrolled('Multi-factor authentication is on.');
            return;
          }
        } catch (recheckErr) {
          if (issued !== generation.current) return;
          if (recheckErr instanceof SessionExpiredError) {
            // The token died between the two calls. The redirect is already
            // running; painting a wrong-code message over a page on its way out
            // would be the last thing the user sees.
            return;
          }
          // Otherwise fall through to the wrong-code message: an unreadable
          // status is no reason to claim success.
        }
        setCodeFailures((count) => count + 1);
        // Deliberately not err.message, and not because the server is wrong.
        // "The verification code is incorrect or has expired" stays literally
        // accurate here: the countdown rules out an expired *session*, but a
        // correct code can still go stale inside its own 30-second step, so
        // "expired" remains a live reading — of the code, not the session.
        //
        // It is replaced because it misdirects. With a setup session visible on
        // screen, "or has expired" invites the user to conclude that session
        // died and they must start over, when both remaining causes — a typo
        // and a stale code — are fixed by reading a fresh code. Choosing our
        // own sentence here is a clarity win, not a truth win.
        setCodeError(WRONG_CODE_MESSAGE);
      } else if (err instanceof InvalidCredentialsError) {
        // The password was accepted at the start call and has not changed
        // since, so this means it was changed elsewhere mid-enrollment.
        setFormError('Your password was changed. Start over to enable it.');
      } else if (err instanceof SessionExpiredError) {
        // Redirect underway.
      } else {
        setFormError(err.message);
      }
    } finally {
      if (issued === generation.current) setBusy(false);
    }
  };

  const startOver = () => {
    reset();
  };

  // The code the user is typing and the error about the last one they sent are
  // the same slot on screen (TotpCodeField gives errorText precedence over
  // helperText), and that slot is where the countdown lives. Leaving a stale
  // error in it would take the clock away for the rest of the dialog's life —
  // and the clock is the only thing that separates a mistyped code from a dead
  // setup session, since the server answers both with 403 INVALID_CODE. Same
  // rule as MfaCodeStep: editing the code retracts the verdict on the old one.
  const handleCodeChange = (next) => {
    setCode(next);
    setCodeError(null);
  };

  const renderPasswordStep = () => (
    <form onSubmit={handleStart}>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          You will need an authenticator app such as Google Authenticator, 1Password, or
          Authy. Confirm your password to begin.
        </DialogContentText>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <TextField
          id="enroll-password"
          name="password"
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={Boolean(passwordError)}
          helperText={passwordError || ' '}
          disabled={busy}
          fullWidth
          autoFocus
          // Focus is on Continue by the time a rejected password comes back,
          // and helper text is only read out when the field it describes is
          // focused — so without a live region the user presses the button and
          // hears nothing. Only while there is an error: the slot otherwise
          // holds a blank spacer, and an empty assertive region is noise.
          slotProps={{ formHelperText: passwordError ? { role: 'alert' } : undefined }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={!password || busy}>
          Continue
        </Button>
      </DialogActions>
    </form>
  );

  const renderVerifyStep = () => (
    <form onSubmit={handleComplete}>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {expired ? (
          <Alert severity="warning">
            This setup session expired before the code was confirmed. Start over to get a
            new QR code.
          </Alert>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Scan this with your authenticator app, then enter the six-digit code it shows.
            </DialogContentText>

            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'common.white' }}>
              <QRCodeSVG value={session.otpauth_uri} size={192} level="M" />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Can&apos;t scan it? Enter this key in your app by hand.
            </Typography>
            <CopyableField id="enroll-secret" label="Setup key" value={session.secret} />
            <CopyableField id="enroll-uri" label="Setup URL" value={session.otpauth_uri} />
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <TotpCodeField
            id="enroll-code"
            label="Six-digit code"
            value={code}
            onChange={handleCodeChange}
            errorText={codeError}
            helperText={
              expired ? ' ' : `Expires in ${formatRemaining(remainingMs)}`
            }
            disabled={busy || expired}
            autoFocus
          />
        </Box>

        {codeFailures >= 2 && !expired && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Still not working? Start over for a new QR code, and check that your device&apos;s
            clock is set automatically.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Link
          component="button"
          type="button"
          onClick={startOver}
          underline="hover"
          sx={{ ml: 1 }}
          // Held shut while a request is in flight, alongside Cancel and Turn
          // on — starting over mid-request throws the dialog back to the
          // password step and then lets the reply land on it. The exception is
          // an expired window: the request that is still out was sent against a
          // session our own clock now says is dead, there is nothing else on
          // this panel the user can do, and the generation check above makes
          // whatever comes back harmless. Same shape as MfaCodeStep, where the
          // expired panel is nothing but this link.
          disabled={busy && !expired}
        >
          Start over
        </Link>
        <Box>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isCompleteCode(code) || busy || expired}
          >
            Turn on
          </Button>
        </Box>
      </DialogActions>
    </form>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Turn on multi-factor authentication</DialogTitle>
      {step === PASSWORD_STEP ? renderPasswordStep() : renderVerifyStep()}
    </Dialog>
  );
}

export default EnrollmentDialog;
